import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { formatPrice } from '../utils/currency';

const RestaurantOrderCreation = ({ apiClient }) => {
  const [tables, setTables] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [orderItems, setOrderItems] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    notes: ''
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [tablesResponse, categoriesResponse, menuItemsResponse, profileResponse] = await Promise.all([
        apiClient.get('/restaurant/tables'),
        apiClient.get('/restaurant/categories'),
        apiClient.get('/restaurant/menu-items'),
        apiClient.get('/restaurant/profile')
      ]);
      
      setTables(tablesResponse.data);
      setCategories(categoriesResponse.data);
      setMenuItems(menuItemsResponse.data);
      setCurrency(profileResponse.data.currency || 'USD');
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
      setCurrency('USD'); // Fallback
    }
  };

  const handleCustomerInfoChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value
    });
  };

  const addItemToOrder = (menuItem) => {
    const existingItem = orderItems.find(item => item.menu_item_id === menuItem.id);
    
    if (existingItem) {
      setOrderItems(orderItems.map(item => 
        item.menu_item_id === menuItem.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setOrderItems([...orderItems, {
        menu_item_id: menuItem.id,
        menu_item_name: menuItem.name,
        price: menuItem.price,
        quantity: 1,
        notes: ''
      }]);
    }
    
    toast.success(`Added ${menuItem.name} to order`);
  };

  const updateItemQuantity = (menuItemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(menuItemId);
    } else {
      setOrderItems(orderItems.map(item => 
        item.menu_item_id === menuItemId 
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const updateItemNotes = (menuItemId, notes) => {
    setOrderItems(orderItems.map(item => 
      item.menu_item_id === menuItemId 
        ? { ...item, notes: notes }
        : item
    ));
  };

  const removeItem = (menuItemId) => {
    setOrderItems(orderItems.filter(item => item.menu_item_id !== menuItemId));
  };

  const getSubtotal = () => {
    return orderItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const submitOrder = async () => {
    if (!selectedTable) {
      toast.error('Please select a table');
      return;
    }
    
    if (orderItems.length === 0) {
      toast.error('Please add items to the order');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        table_id: selectedTable,
        items: orderItems.map(item => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity,
          notes: item.notes
        })),
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        payment_method: "pay_later", // Default for restaurant orders
        notes: customerInfo.notes,
        created_by_restaurant: true
      };

      const response = await apiClient.post('/restaurant/orders', orderData);
      
      toast.success(`Order #${response.data.id.slice(-8)} created successfully!`);
      
      // Reset form
      setOrderItems([]);
      setSelectedTable('');
      setCustomerInfo({ name: '', phone: '', notes: '' });
      
      // Show order summary
      alert(`Order Created Successfully!\n\nOrder #${response.data.id.slice(-8)}\nTotal: ${formatPrice(response.data.total_amount, currency)}\nTable: ${tables.find(t => t.id === selectedTable)?.name}\n\nOrder has been sent to kitchen.`);
      
    } catch (error) {
      console.error('Error creating order:', error);
      
      // Handle specific error messages instead of generic "Failed to create order"
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      
      if (errorMessage.includes('capacity')) {
        toast.error('Restaurant has reached order capacity. Please try again later.');
      } else if (errorMessage.includes('minimum') || errorMessage.includes('convert')) {
        toast.error('Order amount issue. Please check item prices and quantities.');
      } else if (errorMessage.includes('not found')) {
        toast.error('Selected items or table not found. Please refresh and try again.');
      } else if (errorMessage.includes('validation') || errorMessage.includes('required')) {
        toast.error('Please fill in all required fields correctly.');
      } else if (error.response?.status === 401) {
        toast.error('Authentication expired. Please login again.');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to create orders.');
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again or contact support.');
      } else {
        toast.error(`Order creation failed: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Unknown';
  };

  // Group menu items by category
  const itemsByCategory = menuItems.reduce((groups, item) => {
    const categoryId = item.category_id;
    if (!groups[categoryId]) {
      groups[categoryId] = [];
    }
    groups[categoryId].push(item);
    return groups;
  }, {});

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <h2 className="text-xl font-semibold text-gray-900">Create Order for Customer</h2>
          <p className="text-sm text-gray-600 mt-1">
            Create orders on behalf of customers (phone orders, walk-ins, etc.)
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Menu Selection */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Select Items</h3>
            
            {/* Table Selection */}
            <div className="mb-6">
              <label htmlFor="table" className="form-label">
                Select Table *
              </label>
              <select
                id="table"
                value={selectedTable}
                onChange={(e) => setSelectedTable(e.target.value)}
                data-testid="table-select"
                className="form-input"
                required
              >
                <option value="">Choose a table...</option>
                <option value="none">None (No Table)</option>
                {tables.map((table) => (
                  <option key={table.id} value={table.id}>
                    Table {table.table_number} {table.description && `- ${table.description}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Menu Items */}
            <div className="space-y-6 max-h-96 overflow-y-auto">
              {categories.map((category) => {
                const categoryItems = itemsByCategory[category.id] || [];
                if (categoryItems.length === 0) return null;
                
                return (
                  <div key={category.id} data-testid={`category-section-${category.id}`}>
                    <h4 className="font-medium text-gray-900 mb-3">{category.name}</h4>
                    <div className="grid gap-3">
                      {categoryItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                          data-testid={`menu-item-${item.id}`}
                        >
                          <div className="flex-1">
                            <h5 className="font-medium text-gray-900">{item.name}</h5>
                            <p className="text-sm text-gray-500">{item.description}</p>
                            <p className="text-sm font-semibold text-gray-900">{formatPrice(item.price, currency)}</p>
                          </div>
                          <button
                            onClick={() => addItemToOrder(item)}
                            data-testid={`add-item-${item.id}`}
                            className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                            disabled={!item.is_available}
                          >
                            {item.is_available ? 'Add' : 'Unavailable'}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column - Order Summary */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Order Summary</h3>
            
            {/* Customer Information */}
            <div className="mb-6 space-y-4">
              <div>
                <label htmlFor="customer_name" className="form-label">
                  Customer Name (Optional)
                </label>
                <input
                  type="text"
                  id="customer_name"
                  name="name"
                  value={customerInfo.name}
                  onChange={handleCustomerInfoChange}
                  data-testid="customer-name-input"
                  className="form-input"
                  placeholder="Customer name"
                />
              </div>
              <div>
                <label htmlFor="customer_phone" className="form-label">
                  Customer Phone (Optional)
                </label>
                <input
                  type="tel"
                  id="customer_phone"
                  name="phone"
                  value={customerInfo.phone}
                  onChange={handleCustomerInfoChange}
                  data-testid="customer-phone-input"
                  className="form-input"
                  placeholder="Customer phone"
                />
              </div>
              <div>
                <label htmlFor="customer_notes" className="form-label">
                  Order Notes (Optional)
                </label>
                <textarea
                  id="customer_notes"
                  name="notes"
                  value={customerInfo.notes}
                  onChange={handleCustomerInfoChange}
                  data-testid="customer-notes-input"
                  className="form-textarea"
                  rows={2}
                  placeholder="Special instructions..."
                />
              </div>
            </div>

            {/* Order Items */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-3">Items ({orderItems.length})</h4>
              {orderItems.length === 0 ? (
                <p className="text-gray-500 text-sm" data-testid="no-items-message">
                  No items added yet. Select items from the menu.
                </p>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {orderItems.map((item) => (
                    <div key={item.menu_item_id} className="flex items-start justify-between p-3 bg-gray-50 rounded" data-testid={`order-item-${item.menu_item_id}`}>
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-900">{item.menu_item_name}</h5>
                        <p className="text-sm text-gray-600">{formatPrice(item.price, currency)} each</p>
                        <input
                          type="text"
                          placeholder="Item notes..."
                          value={item.notes}
                          onChange={(e) => updateItemNotes(item.menu_item_id, e.target.value)}
                          data-testid={`item-notes-${item.menu_item_id}`}
                          className="mt-1 w-full text-xs px-2 py-1 border border-gray-300 rounded"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => updateItemQuantity(item.menu_item_id, item.quantity - 1)}
                          data-testid={`decrease-${item.menu_item_id}`}
                          className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-sm hover:bg-gray-300"
                        >
                          -
                        </button>
                        <span className="w-8 text-center text-sm font-medium" data-testid={`quantity-${item.menu_item_id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateItemQuantity(item.menu_item_id, item.quantity + 1)}
                          data-testid={`increase-${item.menu_item_id}`}
                          className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-sm hover:bg-gray-300"
                        >
                          +
                        </button>
                        <button
                          onClick={() => removeItem(item.menu_item_id)}
                          data-testid={`remove-${item.menu_item_id}`}
                          className="ml-2 text-xs text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Order Total */}
            {orderItems.length > 0 && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-900">Subtotal:</span>
                  <span className="font-semibold text-gray-900" data-testid="order-subtotal">
                    {formatPrice(getSubtotal(), currency)}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  * Taxes will be calculated automatically
                </p>
                <p className="text-xs text-gray-600">
                  * Payment method: Pay Later (default for restaurant orders)
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={submitOrder}
              disabled={loading || orderItems.length === 0 || !selectedTable}
              data-testid="submit-order-button"
              className="w-full btn-primary"
            >
              {loading ? (
                <div className="spinner mx-auto"></div>
              ) : (
                'Create Order'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantOrderCreation;